import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { EyeIcon, EyeSlashIcon, EnvelopeIcon, LockClosedIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { PageTransition } from '../ui';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Minimum animation duration (3 seconds)
    const minAnimationTime = 3000;
    const startTime = Date.now();

    try {
      await login(email, password);

      // Wait for remaining animation time if login was fast
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minAnimationTime - elapsedTime);
      await new Promise(resolve => setTimeout(resolve, remainingTime));

      // Get user data from localStorage to check role
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        if (user.role === 'ADMIN') {
          navigate('/admin');
        } else {
          navigate('/dashboard');
        }
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTransition className="login-page">
      <div className={`login-wrapper ${loading ? 'is-loading' : ''}`}>
        {/* Background Card */}
        <div className="back-card">
          <img
            src={require('../../assets/images/sales-toast-background.png')}
            alt="Sales Toast background"
            className="back-card-image"
          />
          <div className="back-card-overlay"></div>
          <div className="back-card-content">
            <div className="quote-icon">"</div>
            <p className="back-card-quote">
              Success in sales is the result of discipline, dedication, and an unwavering focus on the customer.
            </p>
            <span className="quote-author">— Sales Toast</span>
          </div>
        </div>

        {/* Main Login Card */}
        <div className="login-card">
          {/* Background Shapes */}
          <div className="card-background">
            <span className="background-shape shape-1"></span>
            <span className="background-shape shape-2"></span>
            <span className="background-shape shape-3"></span>
            <span className="background-shape shape-4"></span>
            <span className="background-shape shape-5"></span>
            <span className="background-shape shape-6"></span>
            <span className="background-shape shape-7"></span>
            <span className="background-shape shape-8"></span>
          </div>

          {/* Logo & Title */}
          <div className="login-header">
            <div className="logo-container">
              <img
                src={require('../../assets/images/sales-toast-logo.png')}
                alt="Sales Toast"
                className="logo-image"
              />
            </div>
            <h1 className="login-title">Welcome Back</h1>
            <p className="login-subtitle">
              Sign in to your Sales Toast command center
            </p>
          </div>

          <form className="login-form" onSubmit={handleSubmit}>
            {/* Email Input */}
            <div className={`input-field ${emailFocused ? 'focused' : ''}`}>
              <EnvelopeIcon className="field-icon" />
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
              />
            </div>

            {/* Password Input */}
            <div className={`input-field ${passwordFocused ? 'focused' : ''}`}>
              <LockClosedIcon className="field-icon" />
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeSlashIcon className="eye-icon" />
                ) : (
                  <EyeIcon className="eye-icon" />
                )}
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="error-message">
                <svg className="error-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="submit-button"
            >
              {loading ? (
                <div className="loading-content">
                  <svg className="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="spinner-track" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="spinner-head" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Signing in...</span>
                </div>
              ) : (
                <>
                  <span className="button-text">Sign In Now</span>
                  <ChevronRightIcon className="button-icon" />
                </>
              )}
            </button>

            {/* Forgot Password */}
            <div className="forgot-password">
              <Link to="/forgot-password">Forgot your password?</Link>
            </div>
          </form>

          {/* Register Link */}
          <div className="register-section">
            <div className="divider">
              <span>New to our platform?</span>
            </div>
            <Link to="/register" className="register-link">
              Partner with us
              <ChevronRightIcon className="link-icon" />
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        .login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%);
          padding: 40px 20px;
          font-family: 'Inter', sans-serif;
        }

        .login-wrapper {
          position: relative;
          width: 100%;
          max-width: 440px;
          display: flex;
          align-items: center;
          justify-content: center;
          perspective: 1200px;
          transform-style: preserve-3d;
        }

        /* Slide animation during loading - back card comes to front */
        .login-wrapper.is-loading .login-card {
          animation: mainCardPush 3s ease-in-out infinite;
        }

        .login-wrapper.is-loading .back-card {
          animation: slideToFront 3s ease-in-out infinite;
        }

        @keyframes mainCardPush {
          0%, 100% { 
            transform: translateX(0) scale(1);
            z-index: 2;
          }
          40%, 60% { 
            transform: translateX(-30px) scale(0.95);
            z-index: 2;
          }
        }

        @keyframes slideToFront {
          0% { 
            transform: rotate(-6deg) translateX(30px) translateY(-20px) scale(0.98);
            z-index: 1;
          }
          30% { 
            transform: rotate(0deg) translateX(80px) translateY(0) scale(1);
            z-index: 3;
          }
          50% { 
            transform: rotate(3deg) translateX(-20px) translateY(10px) scale(1.02);
            z-index: 3;
          }
          70% { 
            transform: rotate(0deg) translateX(80px) translateY(0) scale(1);
            z-index: 3;
          }
          100% { 
            transform: rotate(-6deg) translateX(30px) translateY(-20px) scale(0.98);
            z-index: 1;
          }
        }

        /* Back Card */
        .back-card {
          position: absolute;
          width: 100%;
          height: 100%;
          overflow: hidden;
          transform: rotate(-6deg) translateX(30px) translateY(-20px) scale(0.98);
          box-shadow: 
            0 30px 60px -15px rgba(37, 99, 235, 0.3),
            0 15px 30px -10px rgba(0, 0, 0, 0.15);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1;
          border-radius: 30px 70px 30px 70px;
        }

        .login-wrapper:hover .back-card {
          transform: rotate(-8deg) translateX(35px) translateY(-25px) scale(0.98);
        }

        .back-card-image {
          position: absolute;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .back-card-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            135deg, 
            rgba(37, 99, 235, 0.6) 0%, 
            rgba(30, 64, 175, 0.7) 50%,
            rgba(37, 99, 235, 0.6) 100%
          );
        }

        .back-card-content {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 30px;
          text-align: center;
        }

        .quote-icon {
          font-size: 80px;
          font-family: Georgia, serif;
          color: rgba(255, 255, 255, 0.3);
          line-height: 0.5;
          margin-bottom: 10px;
        }

        .back-card-quote {
          font-size: 16px;
          font-weight: 500;
          color: #ffffff;
          line-height: 1.6;
          margin: 0;
          max-width: 300px;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .quote-author {
          font-size: 13px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.7);
          margin-top: 16px;
          letter-spacing: 0.5px;
        }

        /* Main Login Card */
        .login-card {
          position: relative;
          width: 100%;
          background: #ffffff;
          padding: 48px 40px 56px;
          box-shadow: 
            0 32px 64px -16px rgba(0, 0, 0, 0.14),
            0 16px 32px -8px rgba(0, 0, 0, 0.08),
            0 4px 8px rgba(0, 0, 0, 0.04),
            0 0 0 1px rgba(0, 0, 0, 0.02);
          z-index: 2;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
          border-radius: 80px 24px 50px 24px;
        }

        .login-wrapper:hover .login-card {
          transform: translateY(-4px);
          box-shadow: 
            0 40px 80px -20px rgba(0, 0, 0, 0.18),
            0 20px 40px -10px rgba(0, 0, 0, 0.1),
            0 8px 16px rgba(0, 0, 0, 0.06),
            0 0 0 1px rgba(0, 0, 0, 0.02);
        }

        /* Card Background Shapes */
        .card-background {
          position: absolute;
          inset: 0;
          overflow: hidden;
          z-index: 0;
          pointer-events: none;
          border-radius: 80px 24px 50px 24px;
        }

        .background-shape {
          position: absolute;
          transform: rotate(45deg);
          transition: all 0.6s ease;
        }

        /* Large corner shape - like reference shape1 */
        .shape-1 {
          height: 400px;
          width: 400px;
          background: linear-gradient(135deg, rgba(37, 99, 235, 0.12), rgba(99, 102, 241, 0.06));
          top: -200px;
          right: -150px;
          border-radius: 0 72px 0 0;
          animation: floatSlow 10s ease-in-out infinite;
        }

        /* Tall vertical shape - like reference shape3 */
        .shape-2 {
          height: 350px;
          width: 120px;
          background: linear-gradient(180deg, rgba(37, 99, 235, 0.15), rgba(79, 70, 229, 0.08));
          top: -80px;
          right: -30px;
          border-radius: 32px;
          animation: float 7s ease-in-out infinite;
        }

        /* Bottom shape - like reference shape4 */
        .shape-3 {
          height: 250px;
          width: 140px;
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.12), rgba(22, 163, 74, 0.06));
          bottom: -100px;
          right: 30px;
          border-radius: 60px;
          animation: floatSlow 8s ease-in-out infinite reverse;
        }

        /* Left side accent */
        .shape-4 {
          height: 180px;
          width: 180px;
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(79, 70, 229, 0.06));
          bottom: -60px;
          left: -80px;
          border-radius: 40px;
          animation: float 6s ease-in-out infinite;
        }

        /* Small floating accent */
        .shape-5 {
          height: 80px;
          width: 80px;
          background: linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(217, 119, 6, 0.08));
          top: 35%;
          right: -25px;
          border-radius: 20px;
          animation: floatFast 5s ease-in-out infinite;
        }

        /* Top-left floating shape */
        .shape-6 {
          height: 60px;
          width: 60px;
          background: linear-gradient(135deg, rgba(37, 99, 235, 0.18), rgba(99, 102, 241, 0.1));
          top: 15%;
          left: -15px;
          border-radius: 16px;
          animation: floatFast 4s ease-in-out infinite reverse;
        }

        /* Middle small accent */
        .shape-7 {
          height: 40px;
          width: 40px;
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(22, 163, 74, 0.08));
          top: 55%;
          left: 8%;
          border-radius: 12px;
          animation: pulse 3s ease-in-out infinite;
        }

        /* Bottom-right small accent */
        .shape-8 {
          height: 50px;
          width: 50px;
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(79, 70, 229, 0.08));
          bottom: 20%;
          right: 5%;
          border-radius: 14px;
          animation: floatFast 6s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { transform: rotate(45deg) translateY(0); }
          50% { transform: rotate(45deg) translateY(-15px); }
        }

        @keyframes floatSlow {
          0%, 100% { transform: rotate(45deg) translateY(0) translateX(0); }
          50% { transform: rotate(45deg) translateY(-10px) translateX(5px); }
        }

        @keyframes floatFast {
          0%, 100% { transform: rotate(45deg) translateY(0) scale(1); }
          50% { transform: rotate(45deg) translateY(-20px) scale(1.05); }
        }

        @keyframes pulse {
          0%, 100% { transform: rotate(45deg) scale(1); opacity: 1; }
          50% { transform: rotate(45deg) scale(1.15); opacity: 0.8; }
        }

        /* Login Header */
        .login-header {
          margin-bottom: 36px;
          text-align: center;
        }

        .logo-container {
          width: 80px;
          height: 80px;
          background: #ffffff;
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          box-shadow: 
            0 10px 25px rgba(37, 99, 235, 0.2),
            0 4px 10px rgba(0, 0, 0, 0.1),
            0 0 0 1px rgba(0, 0, 0, 0.02);
          transition: all 0.3s ease;
          overflow: hidden;
        }

        .login-wrapper:hover .logo-container {
          transform: scale(1.05);
          box-shadow: 
            0 14px 30px rgba(37, 99, 235, 0.25),
            0 6px 12px rgba(0, 0, 0, 0.12),
            0 0 0 1px rgba(0, 0, 0, 0.02);
        }

        .logo-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 8px;
        }

        .logo-icon {
          width: 32px;
          height: 32px;
          color: white;
        }

        .login-title {
          font-size: 28px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 8px;
          letter-spacing: -0.5px;
        }

        .login-subtitle {
          font-size: 15px;
          color: #64748b;
          margin: 0;
          font-weight: 500;
        }

        /* Form */
        .login-form {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .input-field {
          position: relative;
          padding: 12px 0;
        }

        .field-icon {
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 20px;
          height: 20px;
          color: #94a3b8;
          transition: color 0.3s ease;
        }

        .input-field.focused .field-icon {
          color: #2563eb;
        }

        .input-field input {
          width: 100%;
          padding: 14px 40px 14px 32px;
          border: none;
          border-bottom: 2px solid #e2e8f0;
          background: transparent;
          font-size: 15px;
          font-weight: 500;
          color: #0f172a;
          transition: all 0.3s ease;
          font-family: inherit;
        }

        .input-field input::placeholder {
          color: #94a3b8;
          font-weight: 400;
        }

        .input-field input:focus {
          outline: none;
          border-bottom-color: #2563eb;
        }

        .input-field input:hover {
          border-bottom-color: #cbd5e1;
        }

        .toggle-password {
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .eye-icon {
          width: 20px;
          height: 20px;
          color: #94a3b8;
          transition: color 0.2s;
        }

        .toggle-password:hover .eye-icon {
          color: #2563eb;
        }

        /* Error Message */
        .error-message {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 16px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 12px;
          animation: shake 0.4s cubic-bezier(.36,.07,.19,.97) both;
        }

        .error-icon {
          width: 20px;
          height: 20px;
          color: #ef4444;
          flex-shrink: 0;
        }

        .error-message span {
          font-size: 14px;
          font-weight: 500;
          color: #dc2626;
        }

        /* Submit Button */
        .submit-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          padding: 16px 24px;
          margin-top: 24px;
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          border: none;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 600;
          letter-spacing: 0.3px;
          color: #ffffff;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 
            0 4px 14px rgba(37, 99, 235, 0.35),
            0 2px 6px rgba(37, 99, 235, 0.2);
          font-family: inherit;
        }

        .submit-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 
            0 8px 20px rgba(37, 99, 235, 0.4),
            0 4px 10px rgba(37, 99, 235, 0.25);
          background: linear-gradient(135deg, #3b82f6, #2563eb);
        }

        .submit-button:active:not(:disabled) {
          transform: translateY(0);
        }

        .submit-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .button-text {
          flex: 1;
          text-align: center;
        }

        .button-icon {
          width: 20px;
          height: 20px;
          color: rgba(255,255,255,0.85);
          transition: transform 0.3s ease;
        }

        .submit-button:hover:not(:disabled) .button-icon {
          transform: translateX(4px);
        }

        .loading-content {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .spinner {
          width: 20px;
          height: 20px;
          animation: spin 1s linear infinite;
        }

        .spinner-track {
          opacity: 0.25;
        }

        .spinner-head {
          opacity: 0.75;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Forgot Password */
        .forgot-password {
          text-align: center;
          margin-top: 16px;
        }

        .forgot-password a {
          font-size: 13px;
          color: #64748b;
          text-decoration: none;
          font-weight: 500;
          transition: all 0.2s;
        }

        .forgot-password a:hover {
          color: #2563eb;
        }

        /* Register Section */
        .register-section {
          margin-top: 32px;
          padding-top: 24px;
          border-top: 1px solid #e2e8f0;
        }

        .divider {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
        }

        .divider span {
          font-size: 13px;
          color: #64748b;
          font-weight: 500;
        }

        .register-link {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 14px 24px;
          background: #f8fafc;
          border: 2px solid #e2e8f0;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 600;
          color: #1e40af;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .register-link:hover {
          background: #eff6ff;
          border-color: #bfdbfe;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.15);
        }

        .link-icon {
          width: 18px;
          height: 18px;
          color: #2563eb;
          transition: transform 0.3s ease;
        }

        .register-link:hover .link-icon {
          transform: translateX(4px);
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }

        /* Responsive */
        @media (max-width: 480px) {
          .login-page {
            padding: 20px 16px;
          }

          .back-card {
            display: none;
          }

          .login-card {
            padding: 36px 24px;
            border-radius: 24px;
          }

          .login-title {
            font-size: 24px;
          }

          .logo-container {
            width: 56px;
            height: 56px;
          }

          .logo-icon {
            width: 28px;
            height: 28px;
          }
        }
      `}</style>
    </PageTransition>
  );
};

export default Login;
